import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import type {
  ConfiguratorBundleResponseBody,
  ConfiguratorFeatureResponseBody,
  ConfiguratorPackageResponseBody,
} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import {isStandardFeature} from './isStandardFeature';

export const WHEEL_GROUP_NAME = 'wheels';

type WheelSetInput = {
  bundle: ConfiguratorBundleResponseBody;
  trimId: string;
  selectedFeatureIds: string[];
  selectedPackageIds: string[];
  autoAddedIds: Set<string>;
  canSelect: boolean;
  factoryFreezeReason?: string;
  /** Writes `featureIds` as is, for going back to the standard wheel (or none). */
  setFeatureIds: (featureIds: string[]) => void;
  /** Picks an upgrade wheel through the step's guarded toggle, with `featureIds` as the result. */
  pickWheel: (wheelId: string, featureIds: string[]) => void;
};

// A build has exactly one wheel set (D63). The trim's STANDARD wheel is included by the trim, like
// every STANDARD feature, so it stays out of `featureIds` (the evaluator rejects it there, D72); an
// upgrade replaces it and picking it clears the upgrade.
// Several wheels in a saved selection show the first upgrade; any wheel pick leaves one.
export function getWheelSet(input: WheelSetInput) {
  const {bundle, selectedFeatureIds, autoAddedIds, factoryFreezeReason} = input;
  const wheels = bundle.features.filter((feature) => feature.kind === 'wheel');
  const wheelIds = new Set(wheels.map(({id}) => id));
  const standardWheel = wheels.find((feature) => isStandardFeature(feature, input.trimId));
  const isPackageInBuild = (item: ConfiguratorPackageResponseBody) =>
    input.selectedPackageIds.includes(item.id) || autoAddedIds.has(item.id);
  const findWheelPackage = (wheelId: string) =>
    bundle.packages.find(
      (item) => isPackageInBuild(item) && item.memberFeatureIds.includes(wheelId)
    );
  // One wheel can fix the set: a wheel a package or rule adds (D70: the evaluator re-adds it and
  // rejects a second upgrade), else a factory wheel picked before the spec freeze.
  const suppliedWheel = wheels.find(
    ({id}) => autoAddedIds.has(id) || isNotNil(findWheelPackage(id))
  );
  const suppliedWheelPackage = isNotNil(suppliedWheel)
    ? findWheelPackage(suppliedWheel.id)
    : undefined;
  const frozenWheel = factoryFreezeReason
    ? wheels.find(({id, fitStage}) => fitStage === 'factory' && selectedFeatureIds.includes(id))
    : undefined;
  const wheelLock = match({suppliedWheel, frozenWheel})
    .with({suppliedWheel: Pattern.nonNullable}, ({suppliedWheel: wheel}) => ({
      id: wheel.id,
      reason: isNotNil(suppliedWheelPackage)
        ? i18n.t('entity.newVehicle.labels.wheelSetFromPackage', {
            defaultValue: 'Wheel set comes with {package}',
            package: suppliedWheelPackage.name,
          })
        : i18n.t('entity.newVehicle.labels.wheelSetAddedAutomatically', {
            defaultValue: '{wheel} was added automatically',
            wheel: wheel.name,
          }),
    }))
    .with({frozenWheel: Pattern.nonNullable}, ({frozenWheel: wheel}) => ({
      id: wheel.id,
      reason: factoryFreezeReason,
    }))
    .otherwise(() => undefined);
  const checkedWheelId =
    wheelLock?.id ??
    wheels.find(({id}) => id !== standardWheel?.id && selectedFeatureIds.includes(id))?.id ??
    standardWheel?.id;
  const withoutWheels = selectedFeatureIds.filter((id) => !wheelIds.has(id));
  // Going back to the standard wheel (or to none) adds nothing and, without a lock, removes no
  // frozen factory wheel, so the spec freeze does not apply.
  const clearWheels = () => {
    if (input.canSelect && isNil(wheelLock)) {
      input.setFeatureIds(withoutWheels);
    }
  };

  return {
    wheelIds,
    standardWheel,
    wheelLock,
    checkedWheelId,
    clearWheels,
    // The selection a click starts from: adding a wheel, or a package that brings one, replaces
    // the picked wheel (one wheel set, D63); only the locked wheel stays.
    getCandidateFeatureIds: (addedFeatureIds: string[]) =>
      addedFeatureIds.some((id) => wheelIds.has(id))
        ? selectedFeatureIds.filter((id) => !wheelIds.has(id) || id === wheelLock?.id)
        : selectedFeatureIds,
    getWheelProps: (feature: ConfiguratorFeatureResponseBody) =>
      wheelIds.has(feature.id)
        ? {
            control: 'radio' as const,
            name: WHEEL_GROUP_NAME,
            isSelected: feature.id === checkedWheelId,
            lockReason:
              isNotNil(wheelLock) && feature.id !== wheelLock.id ? wheelLock.reason : undefined,
          }
        : undefined,
    // A package with another wheel than the fixed one never settles: the evaluator rejects both
    // wheels and the packages re-add them. The same wheel id is no conflict.
    isBlockedByWheelLock: (item: ConfiguratorPackageResponseBody) =>
      isNotNil(wheelLock) &&
      !isPackageInBuild(item) &&
      item.memberFeatureIds.some((id) => wheelIds.has(id) && id !== wheelLock.id),
    selectWheel: (feature: ConfiguratorFeatureResponseBody) =>
      match({isStandard: feature.id === standardWheel?.id, isLocked: isNotNil(wheelLock)})
        .with({isStandard: true}, clearWheels)
        .with({isLocked: false}, () => input.pickWheel(feature.id, [...withoutWheels, feature.id]))
        .otherwise(() => undefined),
  };
}

export type WheelSet = ReturnType<typeof getWheelSet>;
